
import { useState, useEffect, useRef } from 'react';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';

function useInView(threshold = 0.1) {
  const ref = useRef<HTMLDivElement>(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
        }
      },
      { threshold }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [threshold]);

  return { ref, isInView };
}

export default function ContactPage() {
  const [heroLoaded, setHeroLoaded] = useState(false);
  const formSection = useInView(0.2);
  const infoSection = useInView(0.2);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [status, setStatus] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setHeroLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('sending');
    
    // Simulate form submission
    setTimeout(() => {
      setStatus('success');
      setFormData({ name: '', email: '', subject: '', message: '' });
      setTimeout(() => setStatus(''), 3000);
    }, 1000);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative h-[400px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/2fff51d466121b8ca5cd4d0e2305615a.jpg"
            alt="Contact Us"
            className={`w-full h-full object-cover object-center transition-transform duration-[2000ms] ease-out ${heroLoaded ? 'scale-100' : 'scale-110'}`}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>
        <div className={`relative z-10 text-center text-white px-6 transition-all duration-1000 ease-out ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <h1 className="text-5xl font-bold tracking-tight">Contact Us</h1>
        </div>
        
        {/* Animated scroll indicator */}
        <div className={`absolute bottom-8 left-1/2 -translate-x-1/2 transition-all duration-1000 delay-700 ${heroLoaded ? 'opacity-100' : 'opacity-0'}`}>
          <div className="w-6 h-10 border-2 border-white/60 rounded-full flex justify-center">
            <div className="w-1.5 h-3 bg-white/80 rounded-full mt-2 animate-bounce"></div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <div className={`text-center mb-12 transition-all duration-1000 ease-out ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <p className="text-lg text-gray-700">
              Have questions about our products or services? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div 
              ref={formSection.ref}
              className={`transition-all duration-1000 ease-out ${formSection.isInView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`}
            >
              <h2 className="text-2xl font-bold text-gray-900 mb-6 relative inline-block">
                Send us a Message
                <span className={`absolute -bottom-1 left-0 h-1 bg-teal-600 rounded-full transition-all duration-700 delay-300 ${formSection.isInView ? 'w-full' : 'w-0'}`}></span>
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className={`transition-all duration-500 delay-100 ${formSection.isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                    Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all"
                  />
                </div>
                <div className={`transition-all duration-500 delay-200 ${formSection.isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all"
                  />
                </div>
                <div className={`transition-all duration-500 delay-300 ${formSection.isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                    Subject *
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all"
                  />
                </div>
                <div className={`transition-all duration-500 delay-400 ${formSection.isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    maxLength={500}
                    className="w-full px-4 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all resize-none"
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</p>
                </div>
                <button
                  type="submit"
                  disabled={status === 'sending'}
                  className={`w-full bg-teal-600 text-white py-3 px-6 rounded-md font-medium hover:bg-teal-700 hover:scale-[1.02] transition-all duration-300 disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer ${formSection.isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                  style={{ transitionDelay: formSection.isInView ? '500ms' : '0ms' }}
                >
                  {status === 'sending' ? 'Sending...' : 'Send Message'}
                </button>
                {status === 'success' && (
                  <div className="bg-green-50 text-green-700 p-3 rounded-md text-sm text-center animate-pulse">
                    Message sent successfully!
                  </div>
                )}
              </form>
            </div>

            {/* Contact Information */}
            <div 
              ref={infoSection.ref}
              className={`transition-all duration-1000 ease-out ${infoSection.isInView ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`}
            >
              <h2 className="text-2xl font-bold text-gray-900 mb-6 relative inline-block">
                Get in Touch
                <span className={`absolute -bottom-1 left-0 h-1 bg-teal-600 rounded-full transition-all duration-700 delay-300 ${infoSection.isInView ? 'w-full' : 'w-0'}`}></span>
              </h2>
              <div className="space-y-6">
                <div className={`flex items-start transition-all duration-500 delay-100 ${infoSection.isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                  <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0 hover:scale-110 transition-transform duration-300">
                    <i className="ri-mail-line text-teal-600 text-xl"></i>
                  </div>
                  <div className="ml-4">
                    <h3 className="font-semibold text-gray-900 mb-1">Email</h3>
                    <p className="text-gray-700 text-sm">brominesystems@yahoo.com</p>
                  </div>
                </div>

                <div className={`flex items-start transition-all duration-500 delay-200 ${infoSection.isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                  <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0 hover:scale-110 transition-transform duration-300">
                    <i className="ri-phone-line text-teal-600 text-xl"></i>
                  </div>
                  <div className="ml-4">
                    <h3 className="font-semibold text-gray-900 mb-1">Phone</h3>
                    <p className="text-gray-700 text-sm">Available upon request</p>
                  </div>
                </div>

                <div className={`flex items-start transition-all duration-500 delay-300 ${infoSection.isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                  <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0 hover:scale-110 transition-transform duration-300">
                    <i className="ri-time-line text-teal-600 text-xl"></i>
                  </div>
                  <div className="ml-4">
                    <h3 className="font-semibold text-gray-900 mb-1">Business Hours</h3>
                    <p className="text-gray-700 text-sm">Monday - Thursday: 7:00 AM - 2:00 PM</p>
                    <p className="text-gray-700 text-sm">Friday: 7:00 AM - 12:00 PM</p>
                  </div>
                </div>

                <div className={`mt-8 p-6 bg-teal-50 rounded-lg transition-all duration-700 delay-400 ${infoSection.isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                  <h3 className="font-semibold text-gray-900 mb-3">Why Choose Us?</h3>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className={`flex items-start transition-all duration-500 delay-500 ${infoSection.isInView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'}`}>
                      <i className="ri-check-line text-teal-600 mr-2 mt-0.5"></i>
                      <span>Decades of industry experience</span>
                    </li>
                    <li className={`flex items-start transition-all duration-500 delay-[600ms] ${infoSection.isInView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'}`}>
                      <i className="ri-check-line text-teal-600 mr-2 mt-0.5"></i>
                      <span>Trusted by military and commercial sectors</span>
                    </li>
                    <li className={`flex items-start transition-all duration-500 delay-[700ms] ${infoSection.isInView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'}`}>
                      <i className="ri-check-line text-teal-600 mr-2 mt-0.5"></i>
                      <span>Proven reliability in harsh environments</span>
                    </li>
                    <li className={`flex items-start transition-all duration-500 delay-[800ms] ${infoSection.isInView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'}`}>
                      <i className="ri-check-line text-teal-600 mr-2 mt-0.5"></i>
                      <span>Commitment to water safety excellence</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
